import { useContext, useEffect, useRef } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import { KeyCodes, Theme, useIsTheme } from '@afterpaytouch/core'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import {
  getAllShippingOptions,
  getUpdatedShippingOption,
  resetToInitialCheckoutAmount,
  setShippingOptionStatus,
  updateCheckout,
  useAllowedShippingOptions,
  useAppDispatch,
  useCrossBorderTrade,
  useIsPickup,
  useShippingAddress,
  useShippingOption,
  selectAndUpdateShippingOption,
  setShippingOptionError,
  useCheckoutData,
  useOtbAmount,
  useShippingOptionIdentifier,
  useInlineArcadeComponentsEnabled,
  useMerchantName,
  useIsShippingOptionAddressError,
} from '../../state'
import { useMerchantCallback } from '../../utils/merchant'
import {
  ShippingOptionStatus,
  TrackingEvent,
  ShippingOption as ShippingOptionModel,
  ShippingOptionErrorType,
  TOGGLE_STATES,
  ShippingOption,
  ConvergenceTFunction,
} from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { ShippingOptionContext } from './context'
import { SelectedAndSortedShippingOptions, ShippingOptionErrorMessage, ShippingOptionHandlers } from './constants'
import { useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'

export const useRetrieveShippingOptions = (): (() => Promise<void>) => {
  const { selectedShippingOption } = useShippingOption()
  const dispatch = useAppDispatch()
  const isPickup = useIsPickup()
  const { chosenAddress, pickupAddress } = useShippingAddress()
  const callbacks = useMerchantCallback()

  return async () => {
    if (!(selectedShippingOption === undefined || selectedShippingOption === null)) {
      dispatch(resetToInitialCheckoutAmount())
    }

    let shippingAddress

    if (isPickup) {
      shippingAddress = pickupAddress
    } else {
      shippingAddress = Object.assign({}, chosenAddress)
      // Remove fields that would break the API contract for onShippingAddressChange
      delete shippingAddress.id
      delete shippingAddress.preferred
    }

    await dispatch(getAllShippingOptions({ callback: callbacks.onShippingAddressChange, argument: shippingAddress }))
  }
}

export const useChangeShippingOption = (): ((option: ShippingOptionModel) => Promise<void>) => {
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { isCrossBorder } = useCrossBorderTrade()

  const { allShippingOptions } = useShippingOption()
  const callbacks = useMerchantCallback()
  const allowedShippingOptions = useAllowedShippingOptions()

  return async (option: ShippingOptionModel) => {
    logEvent(TrackingEvent.SELECTED_SHIPPING_OPTION, {
      allOptions: allowedShippingOptions,
      shippingOptionName: option,
    })

    const selectedOption = isCrossBorder ? allShippingOptions.find((_option) => _option.id === option.id) : option
    // try {
    // select and update checkout with the shipping option
    // @ts-expect-error TODO: fix type error
    const result = unwrapResult(await dispatch(selectAndUpdateShippingOption(selectedOption)))
    // call if any onShippingOptionChange
    unwrapResult(await dispatch(getUpdatedShippingOption({ callback: callbacks.onShippingOptionChange, argument: selectedOption })))
    // } catch (error) { }
  }
}

export const useUpdateShippingOption = (): (() => Promise<void>) => {
  const dispatch = useAppDispatch()
  const { selectedShippingOption } = useShippingOption()
  const isPickup = useIsPickup()
  const { chosenAddress } = useShippingAddress()

  return async () => {
    try {
      unwrapResult(
        await dispatch(
          updateCheckout({
            amount: selectedShippingOption.orderAmount,
            shippingAddressId: isPickup ? undefined : chosenAddress.id,
            shippingOptionIdentifier: selectedShippingOption.id,
          })
        )
      )
      dispatch(setShippingOptionStatus(ShippingOptionStatus.UpdatedSelected))
    } catch {}
  }
}

export const useShippingOptionError = (): ShippingOptionErrorMessage | null => {
  const dispatch = useAppDispatch()
  const { shippingOptionsStatus, error: shippingOptionError } = useShippingOption()

  const allowedShippingOptions = useAllowedShippingOptions()
  const isShippingOptionLoading = shippingOptionsStatus <= ShippingOptionStatus.Loading
  const showUnableToUpdateError = shippingOptionError === ShippingOptionErrorType.ShippingOptionUnableToUpdate && allowedShippingOptions.length === 1

  const hasAllowedShippingOptions = !(allowedShippingOptions.length === 0 && !isShippingOptionLoading)

  if (showUnableToUpdateError) {
    return { error: shippingOptionError, handleRetryButton: true }
  }
  if (
    shippingOptionsStatus === ShippingOptionStatus.Error &&
    shippingOptionError !== ShippingOptionErrorType.ShippingOptionUnableToUpdate &&
    shippingOptionError !== undefined
  ) {
    return { error: shippingOptionError, handleRetryButton: shippingOptionError === ShippingOptionErrorType.ShippingOptionUpdateInvalid }
  }

  if (!hasAllowedShippingOptions) {
    const totalTooHighError = ShippingOptionErrorType.TotalTooHighToLoadAnyOption
    dispatch(setShippingOptionError(totalTooHighError))
    return { error: totalTooHighError, handleRetryButton: false }
  }

  return null
}

export const useShippingOptionHandlers = (): ShippingOptionHandlers => {
  const { selectedShippingOption } = useShippingOption()
  const isKeyboardEventRef = useRef(false)
  const hasSelectedShippingOption = valueIsNotNullOrUndefined(selectedShippingOption)
  const handleShippingOptionChange = useChangeShippingOption()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const allowedShippingOptions = useAllowedShippingOptions()
  const isAPTheme = useIsTheme(Theme.AP)
  const { isCollapse, setIsCollapse, handleExpandedStateChange } = useContext(ShippingOptionContext)

  const handleRadioItemOnKeyDown = (e: React.KeyboardEvent, option: ShippingOptionModel): void => {
    const { key } = e

    if (key !== KeyCodes.Enter) {
      isKeyboardEventRef.current = true
    }

    if (hasSelectedShippingOption) {
      handleRadioItemOnClick(option.name)
    } else if (key === KeyCodes.ArrowDown || key === KeyCodes.ArrowUp || key === KeyCodes.ArrowLeft || key === KeyCodes.ArrowRight) {
      handleShippingOptionChange(option)
    }

    isKeyboardEventRef.current = false
  }

  const handleRadioItemOnClick = (name: string): void => {
    if (isKeyboardEventRef.current) {
      return
    }
    const newState = !isCollapse
    logEvent(TrackingEvent.TOGGLED_SHIPPING_OPTIONS, {
      allOptions: allowedShippingOptions,
      shippingOptionName: name,
      toggledValue: newState ? TOGGLE_STATES.COLLAPSED : TOGGLE_STATES.EXPANDED,
    })
    isAPTheme ? setIsCollapse(newState) : handleExpandedStateChange()
  }

  const onRadioGroupChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const option = allowedShippingOptions.find((option) => e.target.value === option.id)

    option?.id && handleShippingOptionChange(option)
    !isAPTheme && handleExpandedStateChange()
  }

  return { onRadioGroupChange, handleRadioItemOnClick, handleRadioItemOnKeyDown }
}

const getSelectableOption = (
  selectedShippingOption: ShippingOptionModel,
  crossBorder: boolean,
  xbtAllShippingOptions: ShippingOptionModel[]
): ShippingOptionModel | undefined => {
  if (!selectedShippingOption || !crossBorder) {
    return selectedShippingOption
  }
  return xbtAllShippingOptions.find((option) => option.id === selectedShippingOption.id)
}

export const useSelectedAndSortedShippingOptions = (): SelectedAndSortedShippingOptions => {
  const { isCrossBorder } = useCrossBorderTrade()
  const { xbtAllShippingOptions, selectedShippingOption } = useShippingOption()
  const hasSelectedShippingOption = valueIsNotNullOrUndefined(selectedShippingOption)
  const allowedShippingOptions = useAllowedShippingOptions()
  const isAPTheme = useIsTheme(Theme.AP)

  let option
  isAPTheme && (option = getSelectableOption(selectedShippingOption, isCrossBorder, xbtAllShippingOptions))

  const sortedShippingOptionsList =
    hasSelectedShippingOption && isAPTheme ? [option, ...allowedShippingOptions.filter((item) => item.id !== option?.id)] : [...allowedShippingOptions]

  return {
    sortedShippingOptionsList,
    option,
  }
}

export const useShippingOptionList = (): ShippingOptionModel[] => {
  const { isCrossBorder } = useCrossBorderTrade()
  const { xbtAllShippingOptions, allShippingOptions } = useShippingOption()
  const shippingOptions = isCrossBorder ? xbtAllShippingOptions : allShippingOptions

  return shippingOptions
}

export const useShippingOptionWarningMessage = (): void => {
  const { setIsCollapse, handleExpandedStateChange, setShowWarningMessage, setIsExpanded } = useContext(ShippingOptionContext)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { selectedShippingOption, error: shippingOptionError } = useShippingOption()

  const allowedShippingOptions = useAllowedShippingOptions()
  const isShippingOptionUnableToUpdate = shippingOptionError === ShippingOptionErrorType.ShippingOptionUnableToUpdate
  const checkoutData = useCheckoutData()
  const otbAmount = useOtbAmount()
  const shippingOptions = useShippingOptionList()

  useEffect(() => {
    // show unable to update warning tooltip
    if (isShippingOptionUnableToUpdate && allowedShippingOptions.length > 1) {
      setShowWarningMessage(true)
      setIsCollapse(false)
      handleExpandedStateChange()
    } else {
      setShowWarningMessage(false)
    }
  }, [selectedShippingOption])

  useEffect(() => {
    // show shipping option over limit warning tooltip
    if (allowedShippingOptions.length !== 0 && allowedShippingOptions.length < shippingOptions.length) {
      logEvent(TrackingEvent.VIEWED_SOME_SHIPPING_OPTIONS_OVER_LIMIT, {
        otb: otbAmount,
        merchantOrderLimit: checkoutData?.maximumOrderValue,
      })
      if (!valueIsNotNullOrUndefined(selectedShippingOption) && shippingOptionError !== ShippingOptionErrorType.ShippingOptionUnableToUpdate) {
        setShowWarningMessage(true)
        setIsExpanded(true)
      }
    }
  }, [shippingOptions])
}

export const usePreselectedShippingOption = (): void => {
  const allowedShippingOptions = useAllowedShippingOptions()
  const { shippingOptionsStatus, selectedShippingOption } = useShippingOption()
  const { handleExpandedStateChange, setIsCollapse } = useContext(ShippingOptionContext)
  const handleShippingOptionChange = useChangeShippingOption()
  const shippingOptionIdentifier = useShippingOptionIdentifier()
  const shippingOptions = useShippingOptionList()
  const hasSelectedShippingOption = valueIsNotNullOrUndefined(selectedShippingOption)
  const dispatch = useAppDispatch()

  useEffect(() => {
    // preselect if only one option comes back
    if (allowedShippingOptions.length === 1 && shippingOptionsStatus === ShippingOptionStatus.Loaded) {
      handleShippingOptionChange(allowedShippingOptions[0])
      handleExpandedStateChange()
    }

    if (valueIsNotNullOrUndefined(shippingOptionIdentifier) && shippingOptionsStatus === ShippingOptionStatus.Loaded && allowedShippingOptions.length > 1) {
      const preselectShippingOption = allowedShippingOptions.find((shippingOption) => shippingOption.id === shippingOptionIdentifier)

      if (valueIsNotNullOrUndefined(preselectShippingOption)) {
        preselectShippingOption?.id && handleShippingOptionChange(preselectShippingOption)
        setIsCollapse(true)
        handleExpandedStateChange()
      }
    }
  }, [shippingOptions, allowedShippingOptions])

  useEffect(() => {
    // reset to initial amount if the updated shipping option list does not have the pre-selected shipping option
    if (
      hasSelectedShippingOption &&
      !allowedShippingOptions.some((_option) => _option.id === selectedShippingOption.id) &&
      shippingOptionsStatus === ShippingOptionStatus.UpdatedSelected
    ) {
      dispatch(resetToInitialCheckoutAmount())
    }
  }, [])
}

const normaliseShippingOption = (shippingOptionName: string, t: ConvergenceTFunction): string => {
  const hasKeyword = /\b(?:shipping|delivery)\b/i.test(shippingOptionName)
  if (hasKeyword) {
    return shippingOptionName
  }

  const shipping = t('common:shipping', { defaultValue: '' })
  const append = shippingOptionName === shippingOptionName.toUpperCase() ? shipping.toUpperCase() : shipping

  return `${shippingOptionName} ${append}`
}

export const useSummaryHeaderLabel = (hasAllowedShippingOptions: boolean, selectedShippingOption?: ShippingOption): string => {
  const { t } = useTranslation()
  const isPickup = useIsPickup()
  const merchantName = useMerchantName()
  const allowedShippingOptions = useAllowedShippingOptions()
  const isInlineArcadeComponentEnabled = useInlineArcadeComponentsEnabled()

  if (isInlineArcadeComponentEnabled) {
    return valueIsNotNullOrUndefined(selectedShippingOption) ? normaliseShippingOption(selectedShippingOption.name, t) : t('common:selectShipping')
  }

  const shippingOptionHeading = isPickup ? 'common:pickupDetails' : 'common:shippingOptions'
  return t(shippingOptionHeading, {
    merchantName,
    count: hasAllowedShippingOptions ? allowedShippingOptions.length : 0,
  })
}

export const useSummarySubtextLabel = (selectedShippingOption?: ShippingOption): string => {
  const { t } = useTranslation()
  const { isZeroAmount } = useMoney({})
  const isShippingAddressError = useIsShippingOptionAddressError()
  const isInlineArcadeComponentEnabled = useInlineArcadeComponentsEnabled()

  if (isInlineArcadeComponentEnabled && isShippingAddressError) {
    return t('common:needValidAddressToContinue')
  } else if (isInlineArcadeComponentEnabled) {
    const formattedMoneyText = isZeroAmount(selectedShippingOption?.shippingAmount.amount)
      ? t('common:free')
      : `${selectedShippingOption?.shippingAmount.symbol ?? '$'}${selectedShippingOption?.shippingAmount.amount}`

    return valueIsNotNullOrUndefined(selectedShippingOption)
      ? t('common:shippingOptionsDetails', {
          amount: formattedMoneyText,
          shippingTime: selectedShippingOption.description,
        })
      : t('common:selectShippingToContinue')
  }

  return ''
}
